import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Check, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

/**
 * Hotel switcher shown in the Topbar for users with membership in >1 hotel.
 * Clicking a hotel calls POST /auth/switch-hotel then reloads the app so all
 * cached queries pick up the new active hotel_id. Super admins never see this
 * (they manage all hotels via the /admin/hotels list).
 */
export default function HotelSwitcher() {
    const { user, refresh } = useAuth();
    const [memberships, setMemberships] = useState([]);
    const [active, setActive] = useState(null);
    const [switching, setSwitching] = useState(false);

    useEffect(() => {
        if (!user || user.role === "super_admin") return;
        let cancelled = false;
        api.get("/auth/memberships").then(({ data }) => {
            if (cancelled) return;
            setMemberships(data.memberships || []);
            setActive(data.active_hotel_id || null);
        }).catch(() => { /* silently ignore */ });
        return () => { cancelled = true; };
    }, [user]);

    // Nothing to show for super admin or single-hotel users
    if (!user || user.role === "super_admin") return null;
    if (memberships.length <= 1) return null;

    const activeMembership = memberships.find((m) => m.hotel_id === active) || memberships[0];

    const doSwitch = async (hotel_id, hotel_name) => {
        if (hotel_id === active || switching) return;
        setSwitching(true);
        try {
            await api.post("/auth/switch-hotel", { hotel_id });
            toast.success(`Switched to ${hotel_name}`);
            // Force a full reload so every context/query invalidates
            setTimeout(() => { window.location.href = "/"; }, 200);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Switch failed");
            setSwitching(false);
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid="hotel-switcher-btn"
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1.5 text-[12.5px] font-medium text-foreground hover:bg-slate-50 dark:hover:bg-slate-800 max-w-[220px]"
                    title={activeMembership?.hotel_name}
                >
                    <Building2 size={13} className="text-primary shrink-0" />
                    <span className="truncate">{activeMembership?.hotel_name || "Hotel"}</span>
                    <ChevronDown size={12} className="text-muted-foreground shrink-0" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                sideOffset={6}
                className="min-w-[240px]"
                data-testid="hotel-switcher-menu"
            >
                <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                    Your hotels ({memberships.length})
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {memberships.map((m) => (
                    <DropdownMenuItem
                        key={m.hotel_id}
                        data-testid={`hotel-switcher-${m.hotel_id}`}
                        onSelect={(e) => {
                            e.preventDefault?.();
                            doSwitch(m.hotel_id, m.hotel_name);
                        }}
                        className="flex items-center gap-2 py-2 cursor-pointer"
                    >
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium text-foreground truncate">
                                {m.hotel_name}
                            </div>
                            <div className="text-[10.5px] text-muted-foreground uppercase tracking-wide">
                                {m.role.replace(/_/g, " ")}
                                {m.is_default && <span className="ml-2 text-primary">· default</span>}
                            </div>
                        </div>
                        {m.hotel_id === active && (
                            <Check size={14} className="text-emerald-600 shrink-0" />
                        )}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
